/**
 * VerifyEmailPage Component
 *
 * Email verification page that extracts token from URL and verifies email address.
 */

import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, Mail } from 'lucide-react';
import { verifyEmail } from '../../api/auth';
import { LogoMark } from '../../components/LogoMark';

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link. No token provided.');
      return;
    }

    const verify = async () => {
      try {
        const result = await verifyEmail(token);
        setStatus('success');
        setMessage(result.message || 'Email verified successfully!');
      } catch (err) {
        setStatus('error');
        setMessage(
          err instanceof Error
            ? err.message
            : 'Failed to verify email. The link may have expired or is invalid.'
        );
      }
    };

    verify();
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-surface-base-light dark:bg-surface-base-dark flex items-center justify-center p-4 pt-safe">
      <div className="w-full max-w-md">
        <div className="bg-surface-raised-light dark:bg-surface-raised-dark rounded-xl shadow-card border border-border-subtle-light dark:border-border-subtle-dark p-8">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <LogoMark className="h-10 w-auto" />
          </div>

          <h1 className="text-2xl font-bold text-center text-text-primary-light dark:text-text-primary-dark mb-6">
            Email Verification
          </h1>

          {/* Loading State */}
          {status === 'loading' && (
            <div className="text-center py-8">
              <Loader2 className="w-12 h-12 animate-spin text-primary-500 dark:text-primary-400 mx-auto mb-4" />
              <p className="text-text-secondary-light dark:text-text-secondary-dark">
                Verifying your email address...
              </p>
            </div>
          )}

          {/* Success State */}
          {status === 'success' && (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-950/20 rounded-full mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <p className="text-lg font-medium text-text-primary-light dark:text-text-primary-dark mb-2">
                Email Verified!
              </p>
              <p className="text-text-secondary-light dark:text-text-secondary-dark mb-6">
                {message}
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-700 hover:bg-primary-800 dark:glow-primary dark:hover:glow-primary-hover text-white rounded-lg font-medium shadow-subtle hover:shadow-hover transition-all"
              >
                Continue to Login
              </Link>
            </div>
          )}

          {/* Error State */}
          {status === 'error' && (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 dark:bg-red-950/20 rounded-full mb-4">
                <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <p className="text-lg font-medium text-text-primary-light dark:text-text-primary-dark mb-2">
                Verification Failed
              </p>
              <p className="text-text-secondary-light dark:text-text-secondary-dark mb-6">
                {message}
              </p>
              <div className="space-y-3">
                <Link
                  to="/login"
                  className="block w-full px-6 py-3 bg-primary-700 hover:bg-primary-800 dark:glow-primary dark:hover:glow-primary-hover text-white rounded-lg font-medium shadow-subtle hover:shadow-hover transition-all text-center"
                >
                  Go to Login
                </Link>
                <Link
                  to="/register"
                  className="block w-full px-6 py-3 border border-border-hover-light dark:border-border-hover-dark bg-surface-raised-light dark:bg-surface-base-dark hover:bg-surface-base-light dark:hover:bg-surface-hover-dark text-text-secondary-light dark:text-text-secondary-dark rounded-lg font-medium transition-all text-center"
                >
                  Register New Account
                </Link>
              </div>
            </div>
          )}

          {/* Help Text */}
          <div className="mt-8 pt-6 border-t border-border-subtle-light dark:border-border-subtle-dark text-center">
            <p className="text-sm text-text-tertiary-light dark:text-text-secondary-dark flex items-center justify-center gap-2">
              <Mail className="w-4 h-4" />
              Check your email for verification links
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
